








































































































import { InputType } from '@getaccept/lib-ui';
import PasswordValidator from '@getaccept/lib-shared/src/password-validator/PasswordValidator.vue';
import type { AxiosError } from 'axios';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { RecaptchaAction } from '@getaccept/lib-shared/src/recaptcha/recaptcha-action';
import type { Ref } from '@vue/composition-api';
import { watch, computed, defineComponent, ref } from '@vue/composition-api';
import { ResetPasswordError } from '../types/enums/reset-password-error';
import type { ErrorMessage } from '../../types/error-message';
import { PasswordValidation } from '../../helpers/password-validation';
import type { ResetResponse } from '../../api/login/types/reset-response';
import { LoginService } from '../../api/login/login.service';
import { RecaptchaService } from '../../api/recaptcha/recaptcha.service';
import router from '../../router/router';
import { ErrorKey } from '../../types/enums/error-key';
import { getErrorMessage } from '../../helpers/error-message';

export default defineComponent({
  components: {
    PasswordValidator,
  },
  setup() {
    const newPassword = ref('');
    const newPasswordRepeat = ref('');
    const viewNewPassword = ref(false);
    const viewNewPasswordRepeat = ref(false);
    const errorMessage = ref('');
    const passwordMatchError = ref('');
    const showValidatorError = ref(false);
    const isValidPassword = ref(false);
    const error: Ref<ErrorMessage | null> = ref(null);
    const loading = ref(false);
    const passwordReset = ref(false);
    const userId = ref('');
    const passwordKey = ref('');

    watch(newPassword, () => {
      errorMessage.value = '';
      passwordMatchError.value = '';
    });
    watch(newPasswordRepeat, () => {
      passwordMatchError.value = '';
    });

    const errorInputMessage = computed(() => errorMessage.value || passwordMatchError.value || '');
    const newPasswordInputType = computed(() =>
      viewNewPassword.value ? InputType.Text : InputType.Password
    );
    const newPasswordRepeatInputType = computed(() =>
      viewNewPasswordRepeat.value ? InputType.Text : InputType.Password
    );

    const handleIsValidPassword = (isValid: boolean) => {
      isValidPassword.value = isValid;
    };

    const toggleViewNewPassword = () => {
      viewNewPassword.value = !viewNewPassword.value;
    };

    const toggleViewNewPasswordRepeat = () => {
      viewNewPasswordRepeat.value = !viewNewPasswordRepeat.value;
    };

    const handleLoginClick = () => {
      router.push({ name: 'login' });
    };

    const handleSubmit = () => {
      showValidatorError.value = true;
      errorMessage.value = PasswordValidation.validateNewPassword(
        newPassword.value,
        isValidPassword.value
      );
      passwordMatchError.value = PasswordValidation.validateNewPasswordRepeat(
        newPassword.value,
        newPasswordRepeat.value
      );
      if (errorMessage.value || passwordMatchError.value) {
        return;
      }
      resetPassword({
        newPassword: newPassword.value,
        newPasswordRepeat: newPasswordRepeat.value,
      });
    };
    const resetPassword = async ({
      newPassword,
      newPasswordRepeat,
    }: {
      newPassword: string;
      newPasswordRepeat: string;
    }) => {
      try {
        loading.value = true;
        const { userId, passwordKey } = router.currentRoute.params;
        const recaptchaToken: string = await RecaptchaService.getToken(RecaptchaAction.Reset);
        const data: ResetResponse = await LoginService.reset({
          newPassword,
          newPasswordRepeat,
          passwordKey,
          userId,
          recaptchaToken,
        });
        if (data.status === 0) {
          resetPasswordFailed({ data });
          loading.value = false;
          return;
        }
        resetPasswordSuccess();
        loading.value = false;
      } catch (error) {
        resetPasswordFailed({ requestError: error });
        loading.value = false;
      }
    };

    const resetPasswordFailed = ({
      requestError,
      data,
    }: {
      requestError?: AxiosError;
      data?: ResetResponse;
    }) => {
      const errorMessage: string = data?.error;
      let newError: ErrorMessage;
      switch (errorMessage) {
        case ResetPasswordError.Throttled:
          newError = getErrorMessage(ErrorKey.Throttled);
          break;
        case ResetPasswordError.InvalidId:
        case ResetPasswordError.InvalidKeyOrPassword:
          newError = getErrorMessage(ErrorKey.InvalidKey);
          if (bugsnagClient) {
            bugsnagClient.notify(new Error(JSON.stringify(data)), event => {
              event.groupingHash = JSON.stringify(data);
            });
          }
          break;
        case ResetPasswordError.PasswordWeak:
          newError = getErrorMessage(ErrorKey.WeakPassword);
          break;
        case ResetPasswordError.CouldNotUpdate:
        default:
          newError = getErrorMessage(ErrorKey.Unknown);
          if (bugsnagClient) {
            if (requestError) {
              bugsnagClient.notify(requestError, event => {
                event.groupingHash = requestError;
              });
            } else {
              bugsnagClient.notify(new Error(JSON.stringify(data)), event => {
                event.groupingHash = JSON.stringify(data);
              });
            }
          }
      }

      error.value = newError;
    };

    const resetPasswordSuccess = () => {
      passwordReset.value = true;
    };

    return {
      newPassword,
      newPasswordRepeat,
      viewNewPassword,
      viewNewPasswordRepeat,
      errorMessage,
      passwordMatchError,
      showValidatorError,
      isValidPassword,
      error,
      loading,
      passwordReset,
      userId,
      passwordKey,
      errorInputMessage,
      newPasswordInputType,
      newPasswordRepeatInputType,
      handleIsValidPassword,
      toggleViewNewPassword,
      toggleViewNewPasswordRepeat,
      handleLoginClick,
      handleSubmit,
    };
  },
});
